import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"
import { TOTALS_KEY } from "constants/tableConversion"
import { RawInputData, DataFormat } from "types/tableConversion.types"
import {
  isStatisticType,
  isDtabStatisticType,
  hasTotalsRow,
  getUniqueStatisticTypes,
} from "./tableConversionHelpers"

/**
 * Checks if data matches the special 2-column edge case pattern
 */
const isSpecialEdgeCase = (data: RawInputData): boolean => {
  if (data.data.length !== 2) return false

  const [headersRow, secondRow] = data.data

  return (
    headersRow.length === 2 &&
    secondRow.length === 2 &&
    headersRow[0] === null &&
    headersRow[1] === "Statistic" &&
    secondRow[0] === null &&
    isStatisticType(secondRow[1])
  )
}

/**
 * Checks if data is XTAB_SIMPLE format
 */
const isXtabSimple = (data: RawInputData): boolean => {
  const headersRow = data.data[0] || []
  const secondRow = data.data[1] || []
  const dataRows = data.data.slice(1)

  // Traditional XTAB_SIMPLE with "Totals" as third header
  if (
    headersRow.length === 3 &&
    headersRow[2] === TOTALS_KEY &&
    secondRow.length >= 3 &&
    (secondRow[1] === "n" || isStatisticType(secondRow[1]))
  ) {
    return true
  }

  // XTAB_SIMPLE with null third header and totals row
  if (
    headersRow.length === 3 &&
    headersRow[2] === null &&
    hasTotalsRow(dataRows) &&
    secondRow.length >= 3 &&
    (secondRow[1] === "n" || isStatisticType(secondRow[1]))
  ) {
    const uniqueStatistics = getUniqueStatisticTypes(dataRows)
    return uniqueStatistics.length === 1
  }

  return false
}

/**
 * Checks if data is DTAB format
 */
const isDtab = (data: RawInputData): boolean => {
  const headersRow = data.data[0] || []
  const secondRow = data.data[1] || []
  const dataRows = data.data.slice(1)

  if (headersRow.length !== 3 || headersRow[2] !== null) {
    return false
  }

  if (!isStatisticType(secondRow[1])) {
    return false
  }

  const statType = secondRow[1] as STATISTIC_TYPE
  if (!isDtabStatisticType(statType)) {
    return false
  }

  // Multi-row DTAB case
  if (dataRows.length > 1) {
    const hasMultipleCategories = dataRows.some(
      (row) => row[0] !== null && row[0] !== dataRows[0]?.[0]
    )
    return hasMultipleCategories && secondRow.length >= 3
  }

  // Single-row DTAB case
  return (
    data.data.length === 2 && secondRow.length >= 3 && secondRow[0] === null
  )
}

/**
 * Detects the data format based on structure and content patterns
 */
export const detectDataFormat = (data: RawInputData): DataFormat => {
  if (!data || !Array.isArray(data.data) || data.data.length === 0) {
    return "XTAB_MULTI"
  }

  if (isSpecialEdgeCase(data)) {
    return "XTAB_MULTI"
  }

  if (isXtabSimple(data)) {
    return "XTAB_SIMPLE"
  }

  if (isDtab(data)) {
    return "DTAB"
  }

  return "XTAB_MULTI"
}
